import React from 'react';
import {
  makeStyles,
  tokens,
  Spinner,
} from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';
import { DocumentPreviewContent, EditableField } from './DocumentPreview';
import { apiFetch } from '../lib/apiFetch';
import { formatPhone } from '../lib/phone';
import {
  resolveWithholdingAmount,
} from '../lib/withholding';

interface DocumentDetailsPaneProps {
  documentId: string;
  /** When set, the preview's customer block / number / date render as inline
   *  editable fields and report edits via onFieldChange. The owner (DocumentEdit)
   *  collects them into one-off PDF overrides for the Download action. */
  editable?: boolean;
  onFieldChange?: (field: EditableField, value: string) => void;
  /** Invoices: persists the chosen payment-terms key onto the order. */
  onPaymentTermsChange?: (termKey: string) => void;
}

interface BooqableDocument {
  data: {
    id: string;
    type: string;
    attributes: {
      number: string;
      document_type: string;
      created_at: string;
      status: string;
      finalized: boolean;
      // Booqable document total semantics (see API docs, Documents resource):
      //   grand_total_in_cents          = total EXCL. taxes (after discount)
      //   grand_total_with_tax_in_cents = total INCL. taxes
      // There is NO `subtotal_in_cents` on documents — reading it yields
      // undefined → $0.00. The preview derives the IVU line from these two.
      grand_total_in_cents: number;
      grand_total_with_tax_in_cents?: number;
      paid_in_cents?: number;
      to_be_paid_in_cents?: number;
      deposit_in_cents?: number;
      // Newline-separated billing-address snapshot Booqable updates on each
      // pro forma regeneration.
      address?: string | null;
    };
  };
  processed: {
    customer: {
      name: string;
      email?: string;
      phone?: string;
      address1?: string;
      city?: string;
      zipcode?: string;
      region?: string;
      // PR services-withholding default rate (fraction, e.g. 0.10), spliced on
      // by /booqable/documents/<id> from the Supabase customer. null = not a
      // withholding agent.
      pr_withholding_rate?: number | null;
    };
    order: {
      number: string;
      starts_at: string;
      stops_at: string;
      contract_number?: string | null;
      po_number?: string | null;
      project_name?: string | null;
      solicitation_number?: string | null;
      payment_terms?: string | null;
      is_monthly?: boolean | null;
      delivery_address?: string | null;
      // Per-invoice PR withholding override (fraction). null = inherit the
      // customer default; a value (incl. 0) is an explicit override.
      pr_withholding_rate?: number | null;
      // Manual per-order PR withholding AMOUNT override (whole cents). null =
      // compute from the rate; a value (incl. 0) supersedes the rate outright.
      pr_withholding_amount_cents?: number | null;
    };
    lines: Array<{
      id: string;
      quantity: number;
      title: string;
      price_each_in_cents: number;
      // Line total (price_each × quantity, per company tax setting). Booqable's
      // `lines` resource exposes this as `price_in_cents` — there is NO
      // `charge_in_cents` attribute. `total_in_cents` only appears on finalized
      // document snapshots, so prefer it when present (mirrors pdf/builders.py).
      price_in_cents: number;
      total_in_cents?: number;
      tax_in_cents?: number;
      charge_length?: number;
      charge_label?: string;
      // Free-text per-line note (writable Booqable line attribute).
      extra_information?: string | null;
      item?: {
        name: string;
      };
    }>;
    company: {
      name?: string;
      address?: string;
      phone?: string;
      email?: string;
      logo_url?: string;
      logo_base64?: string;
      tax_id?: string | null;
      uei?: string | null;
      authorized_official_name?: string | null;
      authorized_official_title?: string | null;
    };
  };
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '200px',
    padding: '16px',
  },
  error: {
    padding: '16px',
    textAlign: 'center',
    color: tokens.colorPaletteRedForeground1,
  },
});

const formatDate = (dateString: string | undefined) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const formatDateTime = (dateString: string | undefined) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });
};

// Booqable document_type → our three-way union. Unknown / missing types fall
// through to 'quote' (the catch-all for the non-invoice, non-contract case).
const mapDocType = (raw?: string): 'invoice' | 'contract' | 'quote' => {
  const t = raw?.toLowerCase();
  if (t === 'invoice') return 'invoice';
  if (t === 'contract') return 'contract';
  return 'quote';
};

// Exported so peers (e.g. DocumentEdit) can read the same cached payload via
// the shared queryKey — TanStack Query dedupes when both call sites use this
// hook with the same id, so the fetch runs once per render.
export const documentQueryKey = (documentId: string) => ['document', documentId] as const;

export const useDocumentQuery = (documentId: string | undefined) =>
  useQuery<BooqableDocument>({
    queryKey: documentQueryKey(documentId ?? ''),
    queryFn: async () => {
      const response = await apiFetch(`/booqable/documents/${documentId}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch document: ${response.statusText}`);
      }
      return response.json();
    },
    enabled: !!documentId,
    staleTime: 30 * 1000,
  });

const DocumentDetailsPane: React.FC<DocumentDetailsPaneProps> = ({
  documentId,
  editable,
  onFieldChange,
  onPaymentTermsChange,
}) => {
  const styles = useStyles();
  const {
    data: document,
    isLoading: loading,
    error,
  } = useDocumentQuery(documentId);

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner size="large" label="Loading document..." />
      </div>
    );
  }

  if (error || !document) {
    return (
      <div className={styles.error}>
        <p>{error instanceof Error ? error.message : 'Failed to load document'}</p>
      </div>
    );
  }

  const attrs = document.data.attributes;
  const customer = document.processed.customer;
  const order = document.processed.order;
  const lines = document.processed.lines || [];
  const company = document.processed.company || {};

  // Map document type from Booqable (same mapping as the embed gate above).
  const getDocumentType = () => mapDocType(attrs.document_type);

  // Inclusive days of the order's LIVE rental window (pickup→return) — counts
  // BOTH the delivery and pickup day, so Jun 25 → Jun 30 is 6 days. The Rental
  // Cycle column must track these dates: Booqable freezes each document line's
  // charge_length when the pro forma is generated and never recomputes it on a
  // later reschedule, so the frozen per-line value drifts from the (updated)
  // window shown right beside it. Mirrors pdf/builders._rental_window_days.
  const rentalWindowDays = (() => {
    if (!order.starts_at || !order.stops_at) return 0;
    const d0 = new Date(order.starts_at).getTime();
    const d1 = new Date(order.stops_at).getTime();
    if (isNaN(d0) || isNaN(d1)) return 0;
    const gap = Math.round((d1 - d0) / 86400000);
    return gap > 0 ? gap + 1 : 0;
  })();

  // Check if invoice is pro forma (not finalized)
  const isProForma = getDocumentType() === 'invoice' && !attrs.finalized;

  // Get document number or "Draft invoice" for unfinalized invoices
  const getDocumentNumber = () => {
    if (isProForma) return 'Draft invoice';
    return attrs.number || 'N/A';
  };

  // PR services-withholding (invoices only): resolve the same holdback the
  // react-pdf invoice renders. A manual per-order amount override wins outright
  // (and carries no rate label); otherwise the effective rate (order override
  // else customer default) × the pre-tax subtotal — grand_total_in_cents is the
  // excl-tax total. Same arithmetic as the PDF, so the preview matches it. It's a
  // below-the-line deduction: recognized Total is unchanged, Due drops by it.
  // (A manual amount applies to the order's first invoice in the PDF; this preview
  // isn't invoice-rank aware, so on a multi-invoice order it reflects the amount
  // on whichever invoice is shown — correct for the common single-invoice case,
  // and the PDF stays authoritative.)
  const preTaxSubtotalCents = attrs.grand_total_in_cents || 0;
  const { amountCents: withholdingCents, rateLabel: withholdingRateLabel } =
    getDocumentType() === 'invoice'
      ? resolveWithholdingAmount(
          order.pr_withholding_amount_cents,
          order.pr_withholding_rate,
          customer.pr_withholding_rate,
          preTaxSubtotalCents,
        )
      : { amountCents: 0, rateLabel: undefined };
  const grossDueCents = attrs.to_be_paid_in_cents;

  // Transform data to DocumentPreviewContent format
  const documentData = {
    number: getDocumentNumber(),
    customer: customer.name || 'Unknown Customer',
    customerEmail: customer.email,
    customerPhone: customer.phone,
    customerAddress: [customer.address1, customer.city, customer.region, customer.zipcode]
      .filter(Boolean)
      .join(', ') || undefined,
    // Bill To = the document's snapshotted billing address (Booqable updates
    // this on each pro forma regeneration). Ship To = the order's delivery
    // address (TWM-local). Both render as separate labeled blocks; Ship To
    // is suppressed when identical to Bill To.
    billToAddress: attrs.address || undefined,
    shipToAddress: order.delivery_address || undefined,
    companyName: company.name || 'Tradewinds Mechanical',
    companyAddress: company.address || '',
    companyPhone: formatPhone(company.phone),
    companyEmail: company.email || '',
    companyLogo: company.logo_url || company.logo_base64,
    // Official issue date (`date`) when set — i.e. on a finalized invoice;
    // proformas have no `date` until finalize, so fall back to created_at.
    // Mirrors the PDF serializer (issueDate = date or created_at).
    date: formatDate(attrs.date || attrs.created_at),
    order: order.number || 'N/A',
    // Invoices show the rental period as a date range (no time of day);
    // quotes/contracts keep the precise pickup/return timestamps because
    // the exact hour matters when the customer is signing a rental term.
    pickup: getDocumentType() === 'invoice'
      ? formatDate(order.starts_at)
      : formatDateTime(order.starts_at),
    return: getDocumentType() === 'invoice'
      ? formatDate(order.stops_at)
      : formatDateTime(order.stops_at),
    items: lines.map(line => ({
      quantity: line.quantity || 1,
      name: line.title || line.item?.name || 'Unknown Item',
      // Rental cycle expressed in DAYS. A rental line carries a charge period
      // (Booqable charge_length, in seconds); non-rental lines (delivery, fees,
      // the Extra Day charge) have none and render blank. For rental lines we
      // show the order's LIVE rental window (rentalWindowDays) so the column
      // stays in step with the pickup/return dates shown — the frozen per-line
      // charge_length goes stale on a reschedule and over-states a sub-week
      // rental. Cap at the line's own billing period: a >1-week rental keeps
      // its weekly cycle here while the overflow bills on a separate Extra Day
      // line (showing the full window would double-count). Fall back to
      // charge_length when the window is unknown. Grapple-truck pickups are a
      // live-load service (truck waits while loaded), so they read "Live Load".
      // A monthly-rental order reads "Monthly" on every rental line (a line
      // carries charge_length); non-rental lines (delivery, fees) stay blank.
      duration: /grapple/i.test(line.title || line.item?.name || '')
        ? 'Live Load'
        : line.charge_length
        ? order.is_monthly
          ? 'Monthly'
          : (() => {
            const chargeDays = Math.round(line.charge_length / 86400);
            const shown = rentalWindowDays ? Math.min(rentalWindowDays, chargeDays) : chargeDays;
            return `${shown} ${shown === 1 ? 'day' : 'days'}`;
          })()
        : '',
      price: (line.price_each_in_cents || 0) / 100,
      tax: line.tax_in_cents ? line.tax_in_cents / 100 : undefined,
      total: (line.total_in_cents ?? line.price_in_cents ?? 0) / 100,
      // Free-text per-line note (Booqable extra_information) entered in the
      // order editor — rendered under the line name. Mirrors the PDF path.
      extraDetails: line.extra_information || undefined,
    })),
    // Subtotal = total excl. taxes; totalInclTaxes = total incl. taxes.
    // DocumentPreview derives the IVU row as (totalInclTaxes - subtotal), so
    // these must be the excl-tax and incl-tax totals — mirrors the PDF path's
    // pdf/builders._derive_totals (subtotal = grand_with_tax - tax). Fall back
    // to the excl-tax total when the with-tax field is absent (untaxed doc).
    subtotal: (attrs.grand_total_in_cents || 0) / 100,
    totalInclTaxes: (attrs.grand_total_with_tax_in_cents ?? attrs.grand_total_in_cents ?? 0) / 100,
    paid: attrs.paid_in_cents !== undefined ? attrs.paid_in_cents / 100 : undefined,
    // Due is shown NET of the holdback — the customer pays the reduced balance,
    // matching the invoice PDF and the Pay-Now link.
    due:
      grossDueCents !== undefined
        ? Math.max(0, grossDueCents - withholdingCents) / 100
        : undefined,
    // Below-the-line PR withholding row (magnitude in cents + rate label).
    prWithholdingInCents: withholdingCents > 0 ? withholdingCents : undefined,
    prWithholdingRate: withholdingRateLabel,

    // Invoice payment-terms key (persisted on the order). Seeds the preview's
    // terms dropdown; the PDF/serializer renders the matching label.
    paymentTerms: order.payment_terms || undefined,

    // Government / procurement fields (migration 031). Merged onto the response
    // by /booqable/documents/<id> from Supabase companies + orders. Empty for
    // commercial orders → DocumentPreview omits the formal block.
    contractNumber: order.contract_number || undefined,
    poNumber: order.po_number || undefined,
    projectName: order.project_name || undefined,
    solicitationNumber: order.solicitation_number || undefined,
    authorizedOfficialName: company.authorized_official_name || undefined,
    authorizedOfficialTitle: company.authorized_official_title || undefined,
    contractorUei: company.uei || undefined,
    contractorTaxId: company.tax_id || undefined,
  };

  // The inline HTML preview is the editing surface for every doc type. The
  // owner (DocumentEdit) drives PDF generation from its toolbar Download
  // button, applying the collected edits as one-off overrides.
  return (
    <div className={styles.container}>
      <DocumentPreviewContent
        documentType={getDocumentType()}
        documentData={documentData}
        editable={editable}
        onFieldChange={onFieldChange}
        onPaymentTermsChange={onPaymentTermsChange}
      />
    </div>
  );
};

export default DocumentDetailsPane;
